<template>
    <div class="space-y-2 md:flex md:items-baseline md:justify-between md:space-y-0">
        <div class="flex sm:items-baseline sm:space-x-4">
            <QrHeader :text="trans('header.your-order')" trans="header.your-order"/>
            <QrLink v-if="order.invoice_url" :href="order.invoice_url" :text="trans('link.view-invoice')" trans="link.view-invoice" class="hidden md:block"/>
            <QrLink v-if="order.tracking_url" :href="order.tracking_url" :text="trans('link.track-package')" trans="link.track-package" class="hidden md:block"/>
        </div>
        <p class="text-sm text-gray-600">
            {{ trans('text.order-placed-at')}}
            <time :datetime="order.created_at" class="font-medium text-gray-900" v-text="order.created_at"/>
        </p>
        <QrLink v-if="order.invoice_url" :href="order.invoice_url" :text="trans('link.view-invoice')" trans="link.view-invoice" class="md:hidden"/>
        <QrLink v-if="order.tracking_url" :href="order.tracking_url" :text="trans('link.track-package')" trans="link.track-package" class="md:hidden"/>
    </div>

    <!-- Products -->
    <section aria-labelledby="products-heading" class="mt-6">
        <div class="space-y-8">
            <Message
                v-if="route.query.order === 'success'"
                success
                :header="trans('header.order-placed')"
                header-trans="header.order-placed"
            />

            <div class="border-b border-t border-gray-200 bg-white md:shadow-sm sm:rounded-lg sm:border">
                <div class="px-4 py-6 sm:px-6 lg:grid lg:grid-cols-12 lg:gap-x-8 lg:p-8">
                    <div class="sm:flex lg:col-span-7">
                        <div class="mt-6 sm:mt-0">
                            <h3 class="text-base font-medium text-gray-900">
                                <a :href="product.href">{{ product.name }}</a>
                            </h3>
                            <p class="mt-2 text-sm font-medium text-gray-900" v-text="price"/>
                            <p class="mt-3 text-sm text-gray-500">{{ product.description }}</p>
                        </div>
                    </div>

                    <div class="mt-6 lg:col-span-5 lg:mt-0 flex">
                        <dl class="text-sm sm:ml-auto">
                            <dt class="font-medium text-gray-900" v-text="trans('label.delivery-address')"/>
                            <dd class="mt-3 text-gray-500">
                                <span class="block">{{ order.address }}</span>
                                <span class="block">{{ order.zip_code }} {{ order.city }}</span>
                                <span class="block capitalize">{{ order.country }}</span>
                            </dd>
                        </dl>
                    </div>
                </div>

                <div class="border-t border-gray-200 px-4 py-6 sm:px-6 lg:p-8">
                    <QrParagraph v-if="orderStep === 0" :text="trans('status.order-received')" trans="status.order-received"/>
                    <QrParagraph v-else-if="orderStep === 1" :text="trans('status.order-being-produced')" trans="status.order-being-produced"/>
                    <QrParagraph v-else :text="trans('status.order-has-been-sent')" trans="status.order-has-been-sent"/>

                    <div class="mt-4">
                        <div class="overflow-hidden rounded-full bg-gray-200">
                            <div class="h-2 rounded-full bg-green-500" :style="{
                                width: `${orderStepWidth}%`
                            }"/>
                        </div>
                        <div class="mt-6 hidden grid-cols-3 text-sm font-medium text-gray-600 sm:grid">
                            <div class="text-green-600" v-text="trans('order.ordered')"/>
                            <div :class="[orderStep > 0 ? 'text-green-600' : '', 'text-center']" v-text="trans('order.in-production')"/>
                            <div :class="[orderStep > 1 ? 'text-green-600' : '', 'text-right ']" v-text="trans('order.sent')"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { trans } from "@/libs/helper.js";
import { computed } from "vue";
import QrLink from "@/components/application-ui/content/QrLink.vue";
import QrHeader from "@/components/application-ui/content/QrHeader.vue";
import QrParagraph from "@/components/application-ui/content/QrParagraph.vue";
import {usePriceFormatter} from "@/composables/priceFormatter.js";
import Message from "@/components/application-ui/feedback/Message.vue";
import {useRoute} from "vue-router";

const { formatPrice } = usePriceFormatter();
const props = defineProps({
    qrCode: Object
});

const route = useRoute();

const product = computed(() => props.qrCode.product);
const order = computed(() => props.qrCode.order);

const price = computed(() => formatPrice(order.value.price / 100, order.value.currency));

const orderStep = computed(() => {
    if (props.qrCode.status === 'sent') {
        return 2;
    }

    if (props.qrCode.status === 'in_production') {
        return 1;
    }

    return 0;
});

const orderStepWidth = computed(() => {
    if (orderStep.value === 0) {
        return 20;
    }

    if (orderStep.value === 1) {
        return 50;
    }

    return 100;
});

</script>
